/**
 * @fileoverview Lightweight UI state store.
 *
 * Holds ephemeral UI state that needs to be shared across distant parts
 * of the component tree (e.g. expanded prompt panel ↔ floating timeline).
 */

import { create } from 'zustand'

interface UIState {
  /** Local editing preference, retained when the floating timeline remounts. */
  isMagneticRearrangeEnabled: boolean
  toggleMagneticRearrange: () => void
  /**
   * Whether any expanded prompt overlay is currently open. Derived from a ref
   * count so multiple stacked overlays (e.g. prompt + lyrics on MiniMax Music)
   * keep the floating timeline offset in place until the last one closes.
   */
  isPromptExpanded: boolean
  /** Internal: number of open ExpandedPromptShell instances. */
  _promptExpandedCount: number
  /** Increment on ExpandedPromptShell mount. */
  incrementPromptExpanded: () => void
  /** Decrement on ExpandedPromptShell unmount. Clamped at 0. */
  decrementPromptExpanded: () => void
  /** Resize notice from dropping an image on a set node (canvas). SetUploadPanel shows and clears. */
  lastSetDropResizeInfo: { assetId: string; message: string } | null
  setLastSetDropResizeInfo: (info: { assetId: string; message: string } | null) => void
  /**
   * Effective height (px) of the FloatingTimeline overlay, including its
   * minimized vs. expanded state. Pushed by the page that owns the
   * timeline; read by other floating overlays (e.g. McpActivityLog) so
   * they can sit above the timeline regardless of its current size.
   * 0 when no FloatingTimeline is mounted.
   */
  floatingTimelineHeight: number
  setFloatingTimelineHeight: (height: number) => void
  /**
   * Pixels at the bottom of the canvas container that are covered by the
   * floating timeline overlay. Non-zero only in create mode with the
   * floating timeline active. Absolutely-positioned canvas UI (e.g.
   * SelectionToolbar, EmptyStateActions) reads this to stay above the
   * timeline without prop-drilling.
   */
  canvasBottomInset: number
  setCanvasBottomInset: (inset: number) => void
}

export const useUIStore = create<UIState>(set => ({
  isMagneticRearrangeEnabled: false,
  toggleMagneticRearrange: () => set(s => ({ isMagneticRearrangeEnabled: !s.isMagneticRearrangeEnabled })),
  isPromptExpanded: false,
  _promptExpandedCount: 0,
  incrementPromptExpanded: () =>
    set(s => {
      const next = s._promptExpandedCount + 1
      return { _promptExpandedCount: next, isPromptExpanded: next > 0 }
    }),
  decrementPromptExpanded: () =>
    set(s => {
      const next = Math.max(0, s._promptExpandedCount - 1)
      return { _promptExpandedCount: next, isPromptExpanded: next > 0 }
    }),
  lastSetDropResizeInfo: null,
  setLastSetDropResizeInfo: (info: { assetId: string; message: string } | null) => set({ lastSetDropResizeInfo: info }),
  floatingTimelineHeight: 0,
  setFloatingTimelineHeight: (height: number) => set({ floatingTimelineHeight: height }),
  canvasBottomInset: 0,
  setCanvasBottomInset: (inset: number) => set({ canvasBottomInset: inset }),
}))
